import { useEffect, useState } from 'react'

import { Stack } from '@mui/material'
import moment from 'moment'
import useMount from 'react-use/esm/useMount'
import { match } from 'ts-pattern'

import { Button } from '~/core/components/button'
import { Title } from '~/core/components/title'
import { asMissEvanPagination, defaultPageSize } from '~/core/pagination'
import { TTime, initDateTimeRange } from '~/core/utils/date'
import { forceFormatNumberWith } from '~/core/utils/number'
import { DataBlock } from '~/features/guild/components/data-block'
import { DateRange } from '~/features/guild/components/date-range'
import { GuildGuardWithRole } from '~/features/guild/components/router-guard'
import { Order } from '~/features/guild/components/table/types'
import { DataBlocksTitle, GuildTitle, TableTitle } from '~/features/guild/components/title'
import { DateRangeWrapper, Form } from '~/features/guild/components/wrappers'
import { GUILD_MIDDLE_TITLE } from '~/features/guild/constants'
import { GuildReportDataTable } from '~/features/guild/report/component/report-data-table'
import {
  GUILD_STREAMER_OVERVIEW_DATA_DEFINITIONS,
  GUILD_STREAMER_TABLE_DATA_DEFINITIONS,
  LIMIT_RANGE_OF_DAYS,
} from '~/features/guild/report/constants'
import {
  GuildDataReportListItem,
  GuildDataReportListTableRow,
  GuildDataReportMenu,
  GuildDataReportParams,
  GuildDataReportSearchParams,
} from '~/features/guild/report/types'
import { GuildDataBlock } from '~/features/guild/types'
import { transformReportQueryParamsToSearchParams } from '~/features/guild/utils'
import { Links } from '~/router/links'
import { GuildApi } from '~/services/fm/guild'
import CenterCircularProgress from '~/shared/components/center-circular-progress'
import useAppSearchParam from '~/shared/navigation/use-app-search-params'
import { MilliSeconds } from '~/shared/types/base/units'
import { EGuildRole } from '~/shared/types/services/user'

const DATA_BLOCKS: GuildDataBlock<GuildDataReportMenu>[] = [
  { field: 'open_live_num', title: '开播主播数（人）' },
  { field: 'new_live_num', title: '新主播数（人）' },
  { field: 'listener_duration_avg', title: '人均收听时长（分钟）' },
]

export const GuildReport = () => {
  const [searchParams, setSearchParams] = useAppSearchParam()

  const time = initDateTimeRange({
    excludeToday: true,
    daysRangeLimit: LIMIT_RANGE_OF_DAYS,
    startTime: searchParams.get('start_date')
      ? (moment(searchParams.get('start_date')).valueOf() as MilliSeconds)
      : undefined,
    endTime: searchParams.get('end_date')
      ? (moment(searchParams.get('end_date')).valueOf() as MilliSeconds)
      : undefined,
  })

  const querySearchParams: GuildDataReportSearchParams = {
    start_date: moment(time.startTime).format('YYYY-MM-DD'),
    end_date: moment(time.endTime).format('YYYY-MM-DD'),
    search_creator: searchParams.get('search_creator') ? searchParams.get('search_creator') || '' : undefined,
    search_creator_id: parseInt(searchParams.get('search_creator_id') || '') || undefined,
    search_agent: searchParams.get('search_agent') ? searchParams.get('search_agent') || '' : undefined,
    order: (searchParams.get('order') as Order) || 'asc',
    order_by: (searchParams.get('order_by') as keyof GuildDataReportListItem) || undefined,
    p: parseInt(searchParams.get('p') || '1') || 1,
    pagesize: parseInt(searchParams.get('pagesize') || `${defaultPageSize}`) || defaultPageSize,
  }

  const exportUrl = `${Links.GUILD_DATA_REPORT_EXPORT_API_URL}?${transformReportQueryParamsToSearchParams(
    querySearchParams
  )}`

  const getDataReportQueryParams: GuildDataReportParams = {
    start_date: querySearchParams.start_date,
    end_date: querySearchParams.end_date,
    search_agent: querySearchParams.search_agent,
    s: querySearchParams.search_creator || querySearchParams.search_creator_id?.toString(),
    p: querySearchParams.p,
    pagesize: querySearchParams.pagesize,
    sort: querySearchParams.order_by
      ? `${querySearchParams.order_by}${querySearchParams.order === 'desc' ? '.desc' : ''}`
      : undefined,
  }

  const [reportMenu, setReportMenu] = useState<GuildDataReportMenu>()
  const [getDataReportMenu, reportMenuData] = GuildApi.getDataReportMenu.useLazyQuery()
  const { data: reportData, refetch: refetchReportData } = GuildApi.getDataReport.useQuery(getDataReportQueryParams)

  const reportDataTableRows =
    reportData?.data.map((item) => {
      const row: GuildDataReportListTableRow = {
        ...item,
        id: item.live_id,
      }

      return row
    }) || []

  const pagination = reportData?.pagination && asMissEvanPagination(reportData.pagination)

  const assignDefaultSearchParams = (modifier: (v: GuildDataReportSearchParams) => void) => {
    const params = { ...querySearchParams, p: 1 }
    modifier(params)
    return transformReportQueryParamsToSearchParams(params)
  }

  useMount(() => {
    if (pagination && !Number.isNaN(pagination.page) && querySearchParams.p !== pagination.page) {
      setSearchParams(
        assignDefaultSearchParams((v) => {
          v.p = pagination.page
        })
      )
    }
    if (pagination && !Number.isNaN(pagination.pagesize) && querySearchParams.pagesize !== pagination.pagesize) {
      setSearchParams(
        assignDefaultSearchParams((v) => {
          v.pagesize = pagination.pagesize
        })
      )
    }
    if (querySearchParams.p !== 1) {
      // 接口仅在第一页返回 menu 字段，所以需要单独分离出来
      getDataReportMenu(getDataReportQueryParams)
    }

    refetchReportData()
  })

  useEffect(() => {
    if (reportMenuData.data?.menu) {
      setReportMenu(reportMenuData?.data?.menu)
    }
  }, [reportMenuData.data?.menu])

  useEffect(() => {
    if (reportData?.menu) {
      setReportMenu(reportData?.menu)
    }
  }, [reportData?.menu])

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    refetchReportData()
  }

  const handleTimeRangeChange = (updatedTime: TTime) => {
    setSearchParams(
      assignDefaultSearchParams((v) => {
        v.start_date = moment(updatedTime.startTime).format('YYYY-MM-DD')
        v.end_date = moment(updatedTime.endTime).format('YYYY-MM-DD')
      })
    )
  }

  const handleCreatorIdChange = (searchCreatorId?: number) => {
    setSearchParams(
      assignDefaultSearchParams((v) => {
        v.search_creator_id = searchCreatorId
      })
    )
  }

  const handleCreatorNameChange = (searchCreator?: string) => {
    setSearchParams(
      assignDefaultSearchParams((v) => {
        v.search_creator = searchCreator
      })
    )
  }

  const handleAgentNameChange = (searchAgent?: string) => {
    setSearchParams(
      assignDefaultSearchParams((v) => {
        v.search_agent = searchAgent
      })
    )
  }

  const handleSortChange = (orderBy: keyof GuildDataReportListTableRow) => {
    setSearchParams(
      assignDefaultSearchParams((v) => {
        if (orderBy === v.order_by) {
          v.order = v.order === 'asc' ? 'desc' : 'asc'
        } else {
          // 如果是切换排序字段，那么默认倒序
          v.order = 'desc'
        }
        v.order_by = orderBy
      })
    )
  }

  const handlePageChange = (p: number) => {
    setSearchParams(
      assignDefaultSearchParams((v) => {
        v.p = p
      })
    )
  }

  return (
    <GuildGuardWithRole requireOneOfRole={[EGuildRole.Owner, EGuildRole.Agent]}>
      {match(reportData)
        .with(undefined, () => <CenterCircularProgress />)
        .otherwise(() => (
          <>
            <Title>{['数据中心', GUILD_MIDDLE_TITLE]}</Title>
            <Form onSubmit={handleFormSubmit}>
              <Stack spacing={4}>
                <DateRangeWrapper>
                  <DateRange time={time} limitDays={LIMIT_RANGE_OF_DAYS} onTimeRangeChange={handleTimeRangeChange} />
                  <Button variant="contained" onClick={handleFormSubmit}>
                    查询
                  </Button>
                </DateRangeWrapper>
                <Stack spacing={2}>
                  <DataBlocksTitle>
                    <GuildTitle
                      title="主播数据概况"
                      tooltipTitle="指标定义"
                      tooltipContents={GUILD_STREAMER_OVERVIEW_DATA_DEFINITIONS}
                    />
                  </DataBlocksTitle>
                  <div>
                    {DATA_BLOCKS.map((block) => {
                      const fieldName = block.field as keyof GuildDataReportMenu
                      return (
                        <DataBlock
                          key={block.field}
                          title={block.title}
                          content={forceFormatNumberWith(reportMenu?.[fieldName])}
                        />
                      )
                    })}
                  </div>
                </Stack>
                <Stack spacing={2}>
                  <TableTitle>
                    <GuildTitle
                      title="主播数据列表"
                      tooltipTitle="指标定义"
                      tooltipContents={GUILD_STREAMER_TABLE_DATA_DEFINITIONS}
                      sx={{ flex: '1' }}
                    />
                    {!!reportData?.data.length && (
                      <a href={exportUrl} download target="_blank">
                        <Button variant="contained">导出</Button>
                      </a>
                    )}
                  </TableTitle>
                  <GuildReportDataTable
                    order={querySearchParams.order}
                    orderBy={querySearchParams.order_by}
                    rows={reportDataTableRows}
                    reportDataQuery={querySearchParams}
                    searchAgentName={querySearchParams.search_agent}
                    searchCreatorName={querySearchParams.search_creator}
                    searchCreatorId={querySearchParams.search_creator_id}
                    handleCreatorIdSearch={handleCreatorIdChange}
                    handleCreatorNameSearch={handleCreatorNameChange}
                    handleAgentNameSearch={handleAgentNameChange}
                    handleSortChange={handleSortChange}
                    count={pagination?.page_count || 0}
                    page={querySearchParams.p}
                    onChange={(e, page) => handlePageChange(page)}
                  />
                </Stack>
              </Stack>
            </Form>
          </>
        ))}
    </GuildGuardWithRole>
  )
}
